import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["card", "selectedCards", "spreadPosition", "shuffleButton", "readyPrompt"]
  static values = {
    readingId: String,
    cardBackImageUrl: String
  }

  connect() {
    this.selectedCardNumbers = []

    this.deckOffsetX = 0
    this.dragStartX = 0
    this.dragging = false
    this.hasMoved = false
    this.deckActive = true

    this.baseTransforms = new Map()

    this.draggedCard = null
    this.dragStartY = 0
    this.cardLiftY = 0
    this.isCardDrag = false
    this.cardBaseY = 0

    // For momentum
    this.lastX = 0
    this.lastTime = 0
    this.velocityX = 0

    this.cardTargets.forEach(card => {
      card.style.transition = "none"
    })

    this.computeBaseFan()
    this.applyTransforms()

    setTimeout(() => {
      this.cardTargets.forEach(card => {
        card.style.transition = "transform 0.4s ease"
      })
    }, 50)

    this.shuffleCards()
    this.enableDeckDragging()

    window.addEventListener("resize", this.handleResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
  }

  enableDeckDragging() {
    this.element.addEventListener("pointerdown", this.startDrag)
    this.element.style.touchAction = "pan-y"
  }

  startDrag = (e) => {
    if (!this.deckActive) return

    this.dragStartX = e.clientX
    this.dragStartY = e.clientY
    this.dragging = true
    this.hasMoved = false
    this.isCardDrag = false
    this.draggedCard = e.target.closest(".card")

    if (this.draggedCard) {
      const rect = this.draggedCard.getBoundingClientRect()
      this.cardBaseY = rect.top
    }

    // For momentum
    this.lastX = e.clientX
    this.lastTime = performance.now()
    this.velocityX = 0

    document.addEventListener("pointermove", this.onDrag)
    document.addEventListener("pointerup", this.stopDrag)
    document.addEventListener("pointercancel", this.stopDrag)
  }

  onDrag = (e) => {
    if (!this.dragging) return

    const dx = e.clientX - this.dragStartX
    const dy = e.clientY - this.dragStartY

    // Decide intent
    if (this.draggedCard && !this.isCardDrag) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
        this.isCardDrag = true
        this.draggedCard.style.transition = "none"
        this.draggedCard.style.zIndex = 50
      }
    }

    // Card vertical drag
    if (this.isCardDrag && this.draggedCard) {
      this.cardLiftY = Math.min(0, dy)
      this.applyTransforms(true)
      return
    }

    // Otherwise, normal deck pan
    if (Math.abs(dx) > 2) this.hasMoved = true

    // Track velocity
    const now = performance.now()
    const dt = now - this.lastTime
    if (dt > 0) {
      this.velocityX = (e.clientX - this.lastX) / dt
    }
    this.lastX = e.clientX
    this.lastTime = now

    if (this.hasMoved) {
      this.deckOffsetX += dx
      this.deckOffsetX = this.clampDeckOffset(this.deckOffsetX)
      requestAnimationFrame(() => this.applyTransforms())
    }

    // Only reset dragStartX if card dragging, not deck
    if (!this.isCardDrag) {
      this.dragStartX = e.clientX
    }
  }

  stopDrag = () => {
    if (this.isCardDrag && this.draggedCard) {
      this.tryPlaceDraggedCard(this.draggedCard)
      this.draggedCard.style.zIndex = ""
    }

    // Momentum only if deck pan, not single card
    if (!this.isCardDrag && Math.abs(this.velocityX) > 0) {
      this.applyMomentum()
    }

    this.dragging = false
    this.isCardDrag = false
    this.cardLiftY = 0
    this.draggedCard = null

    this.applyTransforms()

    document.removeEventListener("pointermove", this.onDrag)
    document.removeEventListener("pointerup", this.stopDrag)
    document.removeEventListener("pointercancel", this.stopDrag)
  }

  applyMomentum() {
    let velocity = this.velocityX * 16 // scale to px/frame
    const friction = 0.92

    const step = () => {
      velocity *= friction
      if (Math.abs(velocity) < 0.5) return

      this.deckOffsetX += velocity
      this.deckOffsetX = this.clampDeckOffset(this.deckOffsetX)
      this.applyTransforms()

      requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
  }

  tryPlaceDraggedCard(card) {
    const cardRect = card.getBoundingClientRect()

    this.spreadPositionTargets.forEach((box, index) => {
      if (box.dataset.filled) return

      const boxRect = box.getBoundingClientRect()

      const overlaps =
        cardRect.left < boxRect.right &&
        cardRect.right > boxRect.left &&
        cardRect.top < boxRect.bottom &&
        cardRect.bottom > boxRect.top

      if (overlaps) {
        card.style.opacity = 0
        box.textContent = card.dataset.index
        box.dataset.filled = "true"
        box.dataset.cardId = card.dataset.cardId

        if (!this.selectedCardNumbers.includes(card.dataset.index)) {
          this.selectedCardNumbers.push(card.dataset.index)
        }

        if (this.selectedCardNumbers.length === this.spreadPositionTargets.length) {
          this.onAllCardsSelected()
        }
      }
    })
  }

  clampOffset(x) {
    if (!this.cardTargets.length) return 0
    const container = this.element.querySelector(".cards")
    const firstCard = this.cardTargets[0].getBoundingClientRect()
    const lastCard = this.cardTargets[this.cardTargets.length - 1].getBoundingClientRect()
    const containerWidth = container.offsetWidth

    const leftMost = firstCard.left + x
    const rightMost = lastCard.right + x

    if (leftMost > 0) return x - leftMost
    if (rightMost < containerWidth) return x + (containerWidth - rightMost)
    return x
  }

  computeBaseFan() {
    this.baseTransforms.clear()
    if (!this.cardTargets.length) return

    const container = this.element.querySelector(".cards")
    const containerWidth = container.offsetWidth
    const containerHeight = container.offsetHeight
    const rect = this.cardTargets[0].getBoundingClientRect()
    const cardWidth = rect.width || 96
    const cardHeight = rect.height || 144
    const numCards = this.cardTargets.length

    this.overlapStep = cardWidth * 0.15

    const fanSpread = Math.min(
      containerWidth * 0.9,
      cardWidth + (numCards - 1) * this.overlapStep
    )

    this.startX = (containerWidth - fanSpread) / 2
    this.centerY = (containerHeight - cardHeight) / 2 - 10
    this.centerIndex = Math.floor(numCards / 2)

    this.cardTargets.forEach((card, i) => {
      this.baseTransforms.set(card, { index: i })
    })
  }

  applyTransforms(isDraggingCard = false) {
    const indexShift = -this.deckOffsetX / this.overlapStep

    this.cardTargets.forEach(card => {
      const base = this.baseTransforms.get(card)
      if (!base) return

      const virtualIndex = base.index - indexShift
      const offset = virtualIndex - this.centerIndex

      const x = this.startX + offset * this.overlapStep
      let y = this.centerY + Math.abs(offset) * 2
      const rotate = offset * 2

      if (isDraggingCard && card === this.draggedCard) {
        y += this.cardLiftY
      }

      card.style.transform = `
        translate(${x}px, ${y}px)
        rotate(${rotate}deg)
      `
    })
  }

  clampDeckOffset(x) {
    const shift = -x / this.overlapStep

    const minShift = 0 - this.centerIndex
    const maxShift = (this.cardTargets.length - 1) - this.centerIndex

    const clampedShift = Math.min(Math.max(shift, minShift), maxShift)

    return -clampedShift * this.overlapStep
  }

  handleResize = () => {
    this.deckOffsetX = 0
    this.computeBaseFan()
    this.applyTransforms()
  }

  selectCard(event) {
    if (!this.deckActive) return

    const card = event.currentTarget
    const cardNumber = card.dataset.index

    if (
      this.selectedCardNumbers.includes(cardNumber) ||
      this.selectedCardNumbers.length >= this.spreadPositionTargets.length
    ) return

    this.selectedCardNumbers.push(cardNumber)
    card.classList.add("invisible")

    if (this.selectedCardNumbers.length === 1) {
      this.shuffleButtonTarget.classList.add("invisible")
    }

    const emptyPositions = this.spreadPositionTargets
      .filter(pos => pos.dataset.filled === "false")
      .sort((a, b) => parseInt(a.dataset.index) - parseInt(b.dataset.index))

    if (!emptyPositions.length) return

    const target = emptyPositions[0]
    target.dataset.filled = "true"
    target.dataset.cardId = card.dataset.cardId

    target.style.backgroundImage = `url('${this.cardBackImageUrlValue}')`

    if (this.selectedCardNumbers.length === this.spreadPositionTargets.length) {
      this.onAllCardsSelected()
    }
  }

  updateSpreadBoxes() {
    this.spreadPositionTargets.forEach((box, index) => {
      box.textContent = this.selectedCardNumbers[index] || "\u00A0"
    })
  }

  onAllCardsSelected() {
    this.deckActive = false
    this.cardTargets.forEach(card => card.style.opacity = 0)

    if (this.hasReadyPromptTarget) {
      this.readyPromptTarget.classList.remove("hidden")
    }
  }

  saveCardPositions = async () => {
    if (!this.readyPromptTarget) return

    const cardPositions = this.spreadPositionTargets.map((box) => ({
      spread_position_id: box.dataset.spreadPositionId,
      card_id: parseInt(box.dataset.cardId)
    }))

    await post(`/readings/${this.readingIdValue}/save_card_positions`, {
      body: JSON.stringify({ card_positions: cardPositions }),
      responseKind: "json"
    })

    this.readyPromptTarget.remove()
    window.location.href = `/readings/${this.readingIdValue}/reveal`
  }

  shuffleCards() {
    const container = this.element.querySelector(".cards")
    if (!container) return

    this.deckOffsetX = 0

    const cards = Array.from(this.cardTargets)
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[cards[i], cards[j]] = [cards[j], cards[i]]
    }

    cards.forEach(card => container.appendChild(card))

    requestAnimationFrame(() => {
      this.computeBaseFan()
      this.applyTransforms()
      setTimeout(() => {
        this.selectedCardNumbers = []
        this.updateSpreadBoxes()
        this.cardTargets.forEach(card => {
          card.classList.remove("border-green-500", "border-4")
        })
      }, 100)
    })
  }
}
